/**
 * Shared types for the "Tying Bench" flogger detail redesign (preview behind
 * `?v=bench`). The server pages fetch the flogger doc with the existing GROQ
 * queries and hand a normalized shape to the client bench components.
 */

export interface BenchStep {
  key: string
  imageUrl?: string
  caption?: string
}

export interface BenchStepGroup {
  key: string
  title: string
  /** Group (chapter) intro — localized plain text (`localeText`), trimmed. */
  description?: string
  /** "en" (or another source locale) when `description` fell back from the
   *  page locale — marks the intro's language for screen readers (WCAG
   *  3.1.2). null/undefined when it is in the page locale. */
  descriptionLang?: string | null
  steps: BenchStep[]
}

export interface RopeConsumptionRow {
  variant?: string
  abbreviation?: string
  rope_consumed_in?: number
  rope_consumed_cm?: number
  linear_distance_in?: number
  linear_distance_cm?: number
}

export interface RopeWrapRow {
  rope_diameter?: string
  rope_per_unit?: string
  wraps_per_inch?: string
  amount_to_cover?: string
}

/** A design card in the "Where you'll tie it" / "Needed for" derived section. */
export interface DesignUsageCard {
  id: string
  title: string
  slug: string
  difficulty?: string
  isGated: boolean
  notation?: string
}

/** A prerequisite row on a design's "Before you start" accordion. */
export interface PrereqRow {
  id: string
  abbr?: string
  name: string
  href?: string
  gated?: boolean
}

/** A "Variations" card on a design. */
export interface VariationCard {
  id: string
  title: string
  slug: string
  thumbUrl?: string
  notation?: string
}

/** An Artist's Corner / Blueprint gallery image. */
export interface GalleryImage {
  url: string
  caption?: string
  alt?: string
}

/** Everything both bench configs share. */
export interface BenchCommonProps {
  title: string
  typeLabel: string
  abbreviation?: string
  difficulty?: string
  isGated: boolean
  hasAccess: boolean
  isAuthenticated: boolean
  excerpt?: string
  /** Portable-text body array (locale-resolved) rendered as the Overview. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any[] | null
  /** "en" when `body` fell back to English on a non-English page (else "" /
   *  null) — marks the Overview prose lang="en" for screen readers (WCAG
   *  3.1.2). */
  bodyLang?: string | null
  author?: string
  publishedDate?: string
  vimeoId?: string
  vimeoMinutes?: number | null
  stepGroups: BenchStepGroup[]
  ropeNeeded: string[]
  whereYoullTieIt: DesignUsageCard[]
  contentId: string | null
  userId: string | null
  commentsEnabled: boolean
  pageUrl: string
  /** localStorage key for anonymous/preview progress. */
  progressKey: string
  /** Published knot/component slugs, for the Notation Guide chip links. */
  notationSlugs: string[]
  /** Account preference: start the Details pane collapsed (true) / no preference (null). */
  railInitialCollapsed?: boolean | null
}

export interface BenchComponentProps extends BenchCommonProps {
  kind: 'component'
  ropeConsumption?: RopeConsumptionRow[]
  ropeWrap?: RopeWrapRow[]
  /** true for floggerTool — the "Where you'll tie it" heading becomes "Needed for". */
  isTool: boolean
}

export interface BenchDesignProps extends BenchCommonProps {
  kind: 'design'
  notation?: string
  blueprintUrl?: string
  artistsCorner: GalleryImage[]
  prerequisites: PrereqRow[]
  variations: VariationCard[]
}

/** Difficulty display: stored value → label + on-dark title-bar color. */
export const DIFFICULTY_META: Record<
  string,
  { label: string; onDark: string; onLight: string }
> = {
  // onLight darkened to the 700 shade so the 11px bold label clears WCAG AA
  // 4.5:1 on a white/gray-50 card (the 600 shades were ~3.6–4.3:1 — RAMP serious).
  beginner: { label: 'Easy', onDark: '#7CE0A3', onLight: '#047857' },
  intermediate: { label: 'Intermediate', onDark: '#F2C49A', onLight: '#B45309' },
  advanced: { label: 'Advanced', onDark: '#F4A2A2', onLight: '#B91C1C' },
}

export function difficultyLabel(d?: string): string | null {
  if (!d) return null
  return DIFFICULTY_META[d]?.label ?? d
}
